import { ButtonToolbar, Dropdown } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faSpinner } from '@fortawesome/pro-solid-svg-icons'

import React, { useState } from 'react'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import ProgressReportExport from 'api/administration/courses/ProgressReportExport'
import Folders from 'components/administration/components/folders'
import { SuccessToast, ErrorToast } from 'components/ui/toast/toastsTemplates'
import UploadFile from 'components/ui/modals/UploadFile'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { useAccess } from 'contexts/AccessContext'
import { isExcelFile } from 'helpers'
import CreateButton from '../CreateButton'
import styles from './Actions.module.scss'

interface ActionsProps {
  allSelected: boolean
  selectedRows: any
  searchText: string
  selectedFilters: any
  fastFilters: any
}

export function Actions({
  allSelected,
  selectedRows,
  searchText,
  selectedFilters,
  fastFilters,
}: ActionsProps) {
  const dispatch = useDispatch()
  const {
    total,
    dropdown: { downloadCourseDisabledUsersReport },
  } = useSelector(({ courses }: IMainState) => courses)
  const [show, setShow] = useState(false)
  const [showFile, setShowFile] = useState(false)
  const [error, setError] = React.useState<React.ReactNode>(null)
  const { advancedReport } = useSelector(
    ({ general }: IMainState) => general.permissions
  )

  const { t } = useTranslation()

  const tUpload = useTranslationRankmi({
    prefix: 'administration.monitor.upload',
  })

  const {
    permissions: { all, canDownload, canManageCourses },
  } = useAccess()

  const handleDownloadCommon = (type: string) => {
    dispatch({
      type,
      payload: {
        allSelected,
        selectedRows,
        params: {
          searchText,
          usersFilter: selectedFilters,
          fastFilters,
        },
      },
    })
  }

  const handleDownload = () => {
    handleDownloadCommon(fromActions.COURSES_DOWNLOAD)
  }
  const handleDownloadEvaluations = () => {
    handleDownloadCommon(fromActions.COURSES_DOWNLOAD_EVALUATIONS)
  }
  const handleDownloadDisabledUsers = () => {
    handleDownloadCommon(fromActions.COURSES_DOWNLOAD_DISABLED_USERS)
  }
  const handleModal = () => {
    setShowFile(true)
  }

  const handleClose = () => {
    setShowFile(false)
  }

  const handleFileUpload = (file: File) => {
    setError(null)
    if (!isExcelFile(file)) {
      setTimeout(() => {
        setError(<>{tUpload('modal.error')}</>)
      }, 500)
      return
    }
    if (file) dispatch(fromActions.Actions.uploadMassiveParticipants({ file }))
    setShowFile(false)
  }

  const loading = downloadCourseDisabledUsersReport.loading

  return (
    <ButtonToolbar className={`float-right ${styles.buttons}`}>
      {all && <Folders />}
      {(all || canManageCourses) && <CreateButton />}
      {(all || canDownload) && (
        <Dropdown
          id="dropdown-manage"
          className={styles.dropdownManage}
          alignRight
          show={show || loading}
          onToggle={() => setShow(!show)}
        >
          <Dropdown.Toggle
            id="dropdown-manage-toggle"
            bsPrefix="noCaret"
            variant={'secondary'}
          >
            {t('administration.courses.list.dropdown.actions')}{' '}
            <FontAwesomeIcon icon={faChevronDown} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              disabled={!allSelected && !selectedRows.length}
              onClick={() => handleDownload()}
            >
              {t('administration.courses.list.dropdown.downloadCourseReport', {
                count: !allSelected
                  ? selectedRows.length
                  : total - selectedRows.length,
              })}
            </Dropdown.Item>
            <Dropdown.Item
              disabled={!allSelected && !selectedRows.length}
              onClick={() => handleDownloadEvaluations()}
            >
              {t(
                'administration.courses.list.dropdown.downloadCourseEvaluationsReport',
                {
                  count: !allSelected
                    ? selectedRows.length
                    : total - selectedRows.length,
                }
              )}
            </Dropdown.Item>
            <Dropdown.Item
              disabled={
                (!allSelected && !selectedRows.length) ||
                downloadCourseDisabledUsersReport.loading
              }
              onClick={() => {
                handleDownloadDisabledUsers()
              }}
            >
              {downloadCourseDisabledUsersReport.loading && (
                <FontAwesomeIcon icon={faSpinner} spin className="mr-1 " />
              )}
              {t(
                'administration.courses.list.dropdown.downloadCourseDisabledUsersReport',
                {
                  count: !allSelected
                    ? selectedRows.length
                    : total - selectedRows.length,
                }
              )}
            </Dropdown.Item>
            <Dropdown.Item
              disabled={false}
              onClick={() => {
                handleModal()
              }}
            >
              {t(
                'administration.courses.list.dropdown.importCoursesMassives.actionImportOption'
              )}
            </Dropdown.Item>
            {advancedReport && (
              <Dropdown.Item
                onClick={async () => {
                  try {
                    const { data } = await ProgressReportExport()
                    if (typeof data === 'string') {
                      SuccessToast(
                        t('administration.toastMessages.downSuccess')
                      )
                      return
                    }
                    SuccessToast(t('administration.toastMessages.downloadSent'))
                  } catch {
                    ErrorToast(t('administration.toastMessages.downloadError'))
                  }
                }}
              >
                {t('administration.courses.list.dropdown.progressReportExport')}
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
      <UploadFile
        title={t(
          'administration.courses.list.dropdown.importCoursesMassives.modalTitle'
        )}
        labels={{
          legend: tUpload('modal.legend'),
          addFile: tUpload('modal.addFile'),
          close: t('commons.buttons.close'),
        }}
        show={showFile}
        onClose={() => {
          handleClose()
        }}
        onUpload={handleFileUpload}
        error={error}
      />
    </ButtonToolbar>
  )
}
